import { numericSortWith } from 'core/elements/grid/hooks/useGridSorting'
import type { AllocationCandidateItem } from '../models'
import { VMHAStatusForHosts } from './VMHAStatusCell'

export const getPercentValue = (value, max) => {
  // Prevent NaN and Infinity values from being returned
  if (max === 0 || isNaN(value / max)) {
    return 0
  }
  return (value / max) * 100
}

type UsageStats = {
  current?: number | string
  max?: number | string
  total?: number | string
}

// FR-007: sorts usage-bar columns by the percentage shown, not by the stringified object
const getUsagePercent = (stats?: UsageStats) => {
  if (!stats || typeof stats !== 'object') {
    return null
  }
  const max = Number(stats.max ?? stats.total ?? 0)

  return max > 0 ? getPercentValue(Number(stats.current ?? 0), max) : 0
}

export const usagePercentSort = (prev?: UsageStats, next?: UsageStats) =>
  numericSortWith(getUsagePercent(prev), getUsagePercent(next))

export const getUsageSeverity = (percent: number) => {
  if (percent >= 90) return { variant: 'danger', message: 'Usage at or above 90%' } as const
  if (percent >= 80) return { variant: 'warning', message: 'Usage at or above 80%' } as const
  return { variant: 'success', message: null } as const
}

// Get the VMHA Status for a Host
// When Cluster has VMHA disable show Disabled
// When Cluster has VMHA enabled - And HA Slave role is applied show as Active
// When Cluster has VMHA enabled - And HA Slave role is present but not applied on the host show as Configuring
// When Cluster has VMHA enabled - And HA Slave role is not present show as Failed
export const getVMHAStatusForHost = (host) => {
  return host?.cluster?.vmHighAvailability?.enabled
    ? host?.v1Roles?.includes('pf9-ha-slave')
      ? host?.v1RoleDetails?.['pf9-ha-slave'] === 'applied'
        ? VMHAStatusForHosts.ACTIVE
        : VMHAStatusForHosts.CONFIGURING
      : VMHAStatusForHosts.FAILED
    : VMHAStatusForHosts.DISABLED
}

type CinderPool = {
  name: string
  capabilities: {
    storage_protocol?: string
    volume_backend_name?: string
    total_capacity_gb?: number | 'infinite'
    free_capacity_gb?: number | 'infinite'
    provisioned_capacity_gb?: number
    max_over_subscription_ratio?: number
    allocated_capacity_gb?: number
    thin_provisioning_support?: boolean
  }
}

type BackendUsage = {
  current: number
  max: number
  percent: number
}

type BackendData = {
  backend: string
  storage_protocol?: string
  volume_backend_name?: string
  usage: BackendUsage
  allocation: BackendUsage
  isOverProvisioned?: boolean
  provisionedCapacity?: number
  maxOverSubscriptionRatio?: number
}

/**
 * Aggregates Cinder pools into backend data for display
 * @param pools - Array of Cinder pools to aggregate
 * @returns Array of backend data with usage and allocation information
 */
export function aggregateCinderPools(pools: CinderPool[]): BackendData[] {
  // Map to group pools by backend
  const backendMap: Record<
    string,
    {
      backend: string
      storage_protocol?: string
      volume_backend_name?: string
      total_capacity_gb: number
      free_capacity_gb: number
      provisioned_capacity_gb: number
      max_over_subscription_ratio: number
      allocated_capacity_gb: number
      isOverProvisioned?: boolean
    }
  > = {}

  pools.forEach((pool) => {
    const caps = pool.capabilities
    // -----------------------------
    // Step 1: Identify backend
    // -----------------------------
    const backend = caps.volume_backend_name || pool.name.split('@')[1].split('#')[1] || 'unknown'

    // Normalize capacity (handle "infinite")
    const total = typeof caps.total_capacity_gb === 'number' ? caps.total_capacity_gb : 0
    const free = typeof caps.free_capacity_gb === 'number' ? caps.free_capacity_gb : 0
    const provisioned = caps.provisioned_capacity_gb ?? 0
    const ratio = caps.max_over_subscription_ratio ?? 0
    const allocated = caps.allocated_capacity_gb > 0 ? caps.allocated_capacity_gb : 0 // Ignore negative values in calculation
    const isOverProvisioned =
      (caps?.max_over_subscription_ratio > 1 && caps?.provisioned_capacity_gb !== undefined) ||
      caps?.thin_provisioning_support

    if (!backendMap[backend]) {
      // -----------------------------
      // Step 2: Initialize backend
      // -----------------------------
      backendMap[backend] = {
        backend,
        storage_protocol: caps.storage_protocol,
        volume_backend_name: caps.volume_backend_name,
        total_capacity_gb: total,
        free_capacity_gb: free,
        provisioned_capacity_gb: provisioned,
        max_over_subscription_ratio: ratio,
        allocated_capacity_gb: allocated,
        isOverProvisioned,
      }
    } else {
      const existing = backendMap[backend]

      // -----------------------------
      // Step 3: Deduplicate (use MAX)
      // -----------------------------
      existing.total_capacity_gb = Math.max(existing.total_capacity_gb, total)
      existing.free_capacity_gb = Math.max(existing.free_capacity_gb, free)
      existing.provisioned_capacity_gb = Math.max(existing.provisioned_capacity_gb, provisioned)
      existing.max_over_subscription_ratio = Math.max(existing.max_over_subscription_ratio, ratio)

      // -----------------------------
      // Step 4: Sum allocated (optional)
      // -----------------------------
      existing.allocated_capacity_gb += allocated
    }
  })

  // -----------------------------
  // Step 5: Transform for UI
  // -----------------------------
  return Object.values(backendMap).map((b): BackendData => {
    const total = b.total_capacity_gb
    const free = b.free_capacity_gb
    const provisioned = b.provisioned_capacity_gb
    // Physical usage
    const used = total - free

    return {
      backend: b.backend,
      storage_protocol: b.storage_protocol,
      volume_backend_name: b.volume_backend_name,
      isOverProvisioned: b.isOverProvisioned, // isOverProvisioned indicates if the backend is over-provisioned (THIN Provisioning)
      maxOverSubscriptionRatio: b.max_over_subscription_ratio,
      provisionedCapacity: provisioned,
      usage: {
        current: used,
        max: total,
        percent: getPercentValue(used, total),
      },
      allocation: {
        current: b.allocated_capacity_gb,
        max: total,
        percent: getPercentValue(b.allocated_capacity_gb, total),
      },
    }
  })
}

export const getHostUsageTotals = (hosts = [], ephemeralStorageShared = false) => {
  const usageTotals = hosts.reduce(
    (accum, host) => {
      return {
        compute: {
          current: accum.compute.current + (host.usage?.compute?.current || 0),
          max: accum.compute.max + (host.usage?.compute?.max || 0),
          units: 'GHz',
        },
        ephemeralDisk: {
          current: accum.ephemeralDisk.current + (host.usage?.ephemeralDisk?.current || 0),
          max: accum.ephemeralDisk.max + (host.usage?.ephemeralDisk?.max || 0),
          units: 'GiB',
        },
        memory: {
          current: accum.memory.current + (host.usage?.memory?.current || 0),
          max: accum.memory.max + (host.usage?.memory?.max || 0),
          units: 'GiB',
        },
      }
    },
    {
      compute: {
        current: 0,
        max: 0,
        units: 'GHz',
      },
      ephemeralDisk: {
        current: 0,
        max: 0,
        units: 'GiB',
      },
      memory: {
        current: 0,
        max: 0,
        units: 'GiB',
      },
    },
  )

  // A shared instance storage pool is one disk, so count it once. This assumes every host mounts
  // the same pool; a host with its own local storage could be picked as the pool.
  if (ephemeralStorageShared) {
    const first = hosts.map((h) => h.usage?.ephemeralDisk).find((d) => d?.max > 0)
    usageTotals.ephemeralDisk = {
      current: first?.current || 0,
      max: first?.max || 0,
      units: 'GiB',
    }
  }

  return {
    compute: {
      ...usageTotals.compute,
      percent: getPercentValue(usageTotals.compute.current, usageTotals.compute.max),
    },
    ephemeralDisk: {
      ...usageTotals.ephemeralDisk,
      percent: getPercentValue(usageTotals.ephemeralDisk.current, usageTotals.ephemeralDisk.max),
    },
    memory: {
      ...usageTotals.memory,
      percent: getPercentValue(usageTotals.memory.current, usageTotals.memory.max),
    },
  }
}

export const getHostAllocationTotals = ({
  hosts = [],
  hypervisors = [],
  allocationCandidates = [] as AllocationCandidateItem[],
  ephemeralStorageShared = false,
}) => {
  const hostIdSet = new Set(hosts.map((host) => host?.id))
  const hostHypervisors = hypervisors.filter((hypervisor) =>
    hostIdSet.has(hypervisor?.service?.host),
  )

  // Build hostname → candidate lookup for ratio-adjusted capacity.
  // Falls back to Nova physical values when Placement data is unavailable.
  const candidatesByHostname = allocationCandidates.reduce(
    (acc, c) => {
      acc[c.hostname] = c
      return acc
    },
    {} as Record<string, AllocationCandidateItem>,
  )

  const hypervisorAllocation = hostHypervisors.reduce(
    (accum, hypervisor) => {
      const candidate = candidatesByHostname[hypervisor.hypervisor_hostname]
      return {
        compute: {
          current:
            accum.compute.current +
            (Number.isFinite(candidate?.vcpuUsed) ? candidate.vcpuUsed : hypervisor.vcpus_used),
          max: accum.compute.max + (candidate ? candidate.vcpuCapacity : hypervisor.vcpus),
          units: 'vCPUs',
        },
        memory: {
          // candidate.memoryCapacity/memoryUsed are in MiB — same unit as memory_mb, divide by 1024 for GiB
          current:
            accum.memory.current +
            (Number.isFinite(candidate?.memoryUsed)
              ? candidate.memoryUsed
              : hypervisor.memory_mb_used) /
              1024,
          max:
            accum.memory.max + (candidate ? candidate.memoryCapacity : hypervisor.memory_mb) / 1024,
          units: 'GiB',
        },
        ephemeralStorage: {
          current: accum.ephemeralStorage.current + hypervisor.local_gb_used,
          max: accum.ephemeralStorage.max + hypervisor.local_gb,
          units: 'GB',
        },
      }
    },
    {
      compute: {
        current: 0,
        max: 0,
      },
      memory: {
        current: 0,
        max: 0,
      },
      ephemeralStorage: {
        current: 0,
        max: 0,
      },
    },
  )
  // A shared pool is one disk that every host reports as local_gb, so count its size once. Same
  // homogeneous-cluster assumption as getHostUsageTotals.
  // local_gb_used is per host (disk used by that host's VMs), so used is still summed.
  if (ephemeralStorageShared) {
    const first = hostHypervisors.find((h) => h?.local_gb > 0)
    hypervisorAllocation.ephemeralStorage = {
      current: hypervisorAllocation.ephemeralStorage.current,
      max: first?.local_gb || 0,
      units: 'GB',
    }
  }
  const hypervisorAllocationWithPercent = {
    compute: {
      ...hypervisorAllocation.compute,
      percent: getPercentValue(
        hypervisorAllocation.compute.current,
        hypervisorAllocation.compute.max,
      ),
    },
    memory: {
      ...hypervisorAllocation.memory,
      percent: getPercentValue(
        hypervisorAllocation.memory.current,
        hypervisorAllocation.memory.max,
      ),
    },
    ephemeralStorage: {
      ...hypervisorAllocation.ephemeralStorage,
      percent: getPercentValue(
        hypervisorAllocation.ephemeralStorage.current,
        hypervisorAllocation.ephemeralStorage.max,
      ),
    },
  }
  return hypervisorAllocationWithPercent
}

// FR-010: a new host has no roles yet
export const isNewHost = (host?: { roles?: string[]; v1Roles?: string[] }): boolean =>
  !host?.roles?.length && !host?.v1Roles?.length

// Text for the passthrough/vgpu message in cluster option tooltips
export const getGpuLabel = (mode: string): string => {
  return mode === 'passthrough' ? 'GPU Passthrough' : mode === 'vgpu' ? 'vGPU' : mode
}

interface ClusterOption {
  disabled: boolean
  tooltip?: string
  icon?: 'circle-exclamation' | 'triangle-exclamation'
}

// EHD-FR-002: shared with the Enroll Hosts drawer cluster step
interface ClusterLike {
  name?: string
  gpu?: { enabled?: boolean; mode?: string }
}

interface GpuHostLike {
  gpuModels?: unknown[]
  compatibleModes?: string[]
}

export const clusterOptionFor = (
  cluster: ClusterLike | undefined,
  host: GpuHostLike | undefined,
): ClusterOption => {
  const isGPUSupportedHost = host?.gpuModels?.length > 0
  if (!isGPUSupportedHost && cluster?.gpu?.enabled) {
    // non GPU host & GPU cluster
    return {
      disabled: true,
      tooltip:
        'No GPU was detected on this host. The host may not be assigned to this GPU cluster.',
      icon: 'circle-exclamation',
    }
  }
  if (
    isGPUSupportedHost &&
    cluster?.gpu?.enabled &&
    !host?.compatibleModes?.includes(cluster?.gpu?.mode)
  ) {
    // GPU host & incompatible GPU cluster
    return {
      disabled: true,
      tooltip: `${getGpuLabel(
        cluster?.gpu?.mode,
      )} is required on the host to assign it to this GPU cluster.`,
      icon: 'circle-exclamation',
    }
  }
  if (isGPUSupportedHost && !cluster?.gpu?.enabled) {
    // GPU host for non-GPU cluster
    return {
      disabled: false,
      tooltip:
        'This host has a GPU. If assigned to this cluster, VMs created on this host will not be able to use its GPU.',
      icon: 'triangle-exclamation',
    }
  }
  // GPU host for compatible cluster or non GPU host for non GPU cluster
  return { disabled: false }
}

export const CPU_ALLOCATION_RATIO_TOOLTIP =
  'Specifies the number of vCPUs to allocate per physical core. For example, an allocation ratio of 1 implies no overcommitment, while an allocation ratio of 2 indicates two vCPUs allocated per physical core.'
export const MEMORY_ALLOCATION_RATIO_TOOLTIP =
  'Specifies how much RAM to allocate relative to the host’s physical RAM. For example, an allocation ratio of 1 implies no overcommitment, while an allocation ratio of 2 indicates allocating twice the amount of the host’s physical RAM.'
export const EPHEMERAL_DISK_ALLOCATION_RATIO_TOOLTIP =
  'Specifies the amount of ephemeral disk space to allocate per gigabyte of physical disk storage. For example, an ephemeral disk allocation ratio of 1 implies no overcommitment, while an allocation ratio of 2 indicates allocating twice the amount of the host’s physical disk storage.'

// Prometheus labels disk series by fstype; the shared instance mount is the host's
// mounted_nfs entry at the blueprint VM storage path (not the Cinder mount, which can be nfs4).
const trimSlash = (path = '') => path.replace(/\/+$/, '')
export const getInstanceStorageFstype = (hosts = [], vmStorage = '') => {
  // Any host may not have reported mounted_nfs yet, so use the first one that has the mount.
  const entry = (hosts || [])
    .flatMap((host) => host?.extensions?.mounted_nfs?.data?.mounted || [])
    .find((m) => trimSlash(m?.destination) === trimSlash(vmStorage))
  return entry?.fstype || 'nfs'
}
